import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  LinearProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { claimAccount } from '../../services/instagramShowcaseService';
import { supabase } from '../../services/supabase';
import TermsDialog from '../onboarding/TermsDialog';
import PrivacyPolicyDialog from '../onboarding/PrivacyPolicyDialog';

interface InstagramShowcaseFlowData {
  email?: string;
  instagramOnboardingToken?: string | null;
  instagramUsername?: string | null;
  showcaseIds?: string[];
  accountType?: 'scraped' | 'existing' | 'new';
  instagramConnected?: boolean;
  instagramToken?: string | null;
  instagramUserId?: string | null;
  instagramPosts?: Array<{ id: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface AccountClaimStepProps {
  flowData: InstagramShowcaseFlowData;
  onUpdate: (data: Partial<InstagramShowcaseFlowData>) => void;
  onNext: () => void;
  onBack: () => void;
  onError: (message: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

interface ClaimFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

interface PasswordStrengthChecks {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

interface PasswordStrengthResult {
  strength: number;
  checks: PasswordStrengthChecks;
}

const AccountClaimStep = ({
  flowData,
  onUpdate,
  onNext,
  onBack,
  onError,
  isLoading,
  setIsLoading
}: AccountClaimStepProps): React.JSX.Element => {
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  // AI Analysis state - REMOVED: No longer polling, will be checked before profile step

  // Terms and Privacy state
  const [isTncAccepted, setIsTncAccepted] = useState<boolean>(false);
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState<boolean>(false);
  const [isTncDialogOpen, setIsTncDialogOpen] = useState<boolean>(false);
  const [isPrivacyPolicyDialogOpen, setIsPrivacyPolicyDialogOpen] = useState<boolean>(false);

  const { control, handleSubmit, formState: { errors }, watch } = useForm<ClaimFormData>({
    defaultValues: {
      email: flowData.email || '',
      password: '',
      confirmPassword: ''
    }
  });

  const watchedPassword = watch('password');
  const watchedEmail = watch('email');

  // Password strength calculation
  const calculatePasswordStrength = (password: string): PasswordStrengthResult => {
    const checks: PasswordStrengthChecks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const strength = Object.values(checks).filter(Boolean).length;
    return { strength, checks };
  };

  const passwordStrength = calculatePasswordStrength(watchedPassword || '');
  const strengthColors: Array<'error' | 'warning' | 'info' | 'success'> = ['error', 'error', 'warning', 'info', 'success', 'success'];
  const strengthLabels: string[] = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];

  const handleClaimAccount = async (data: ClaimFormData): Promise<void> => {
    if (!isTncAccepted || !isPrivacyPolicyAccepted) {
      onError(t('instagramShowcase.claim.acceptTerms', 'Please accept the terms and conditions and privacy policy'));
      return;
    }

    setIsLoading(true);
    onError('');

    try {
      // Step 1: Create Supabase auth account
      const { error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/email-verification-success`,
        }
      });

      if (signUpError) {
        throw new Error(signUpError.message);
      }

      // Step 2: Claim the temporary account
      const claimData = await claimAccount({
        email: data.email,
        password: data.password,
        instagramOnboardingToken: flowData.instagramOnboardingToken ?? undefined
      });

      if (claimData.success) {
        // Clear God Mode session state before new authentication
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonated_user');
        sessionStorage.removeItem('godmode_original_user');
        sessionStorage.removeItem('godmode_original_token');

        // Store the new auth token
        const { token, user, businessId, staffId } = claimData;
        if (token) localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));

        // Update flow data with new authentication token
        onUpdate({
          email: data.email,
          password: data.password,
          claimed: true,
          user: user,
          authToken: token, // Permanent authentication token (30 days)
          businessId: businessId,
          staffId: staffId
        });

        toast.success(t('instagramShowcase.claim.success', 'Account claimed successfully! Check your email for verification.'));
        onNext();
      } else {
        throw new Error(claimData.message || 'Failed to claim account');
      }

    } catch (error: unknown) {
      console.error('Account claim error:', error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.message || 'Failed to claim account';

      // Check if token is expired or invalid
      if (errorMessage.includes('expired') || errorMessage.includes('Invalid or expired')) {
        console.log('Instagram token expired, clearing local storage');
        localStorage.removeItem('instagram_onboarding_token');

        // Reset flow data to force reconnection
        onUpdate({
          instagramConnected: false,
          instagramToken: null,
          instagramOnboardingToken: null,
          instagramUserId: null,
          instagramUsername: undefined,
          instagramPosts: []
        });

        toast.error(t('instagramShowcase.claim.tokenExpired', 'Instagram session expired. Please reconnect your Instagram account.'));
      } else {
        onError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get account type messaging
  const accountType = flowData.accountType || 'new';
  const getAccountTypeTitle = (): string => {
    switch(accountType) {
      case 'scraped':
        return t('instagramShowcase.claim.titleScraped', 'Claim Your Existing Account');
      case 'existing':
        return t('instagramShowcase.claim.titleExisting', 'Welcome Back!');
      case 'new':
      default:
        return t('instagramShowcase.claim.title', 'Create Your Account');
    }
  };

  const getAccountTypeDescription = (): string => {
    switch(accountType) {
      case 'scraped':
        return t('instagramShowcase.claim.descriptionScraped', 'We found your salon profile! Enter your email to claim it and start managing your showcases.');
      case 'existing':
        return t('instagramShowcase.claim.descriptionExisting', 'This Instagram account is already connected. Enter your credentials to continue.');
      case 'new':
      default:
        return t('instagramShowcase.claim.description', 'Your showcases are ready! Now create your account to manage them and start receiving bookings.');
    }
  };

  const getAccountTypeAlert = (): React.JSX.Element | null => {
    if (accountType === 'scraped') {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('instagramShowcase.claim.scrapedAccountInfo', 'Your business profile already exists in our system. By claiming it, you\'ll gain full control and access to all existing showcases and data.')}
          </Typography>
        </Alert>
      );
    } else if (accountType === 'existing') {
      return (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('instagramShowcase.claim.existingAccountInfo', 'Reconnecting to your existing account. Your previous data and settings will be preserved.')}
          </Typography>
        </Alert>
      );
    }
    return null;
  };

  return (
    <Box className="account-claim-step">
      <Typography variant="h5" gutterBottom>
        {getAccountTypeTitle()}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {getAccountTypeDescription()}
      </Typography>

      {getAccountTypeAlert()}

      <Divider sx={{ my: 3 }} />

      {/* Account Creation Summary */}
      <Card elevation={1} sx={{ mb: 3, bgcolor: 'rgba(76, 175, 80, 0.04)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'success.main', mb: 2 }}>
            {accountType === 'scraped'
              ? t('instagramShowcase.claim.existingProfileFound', "Existing Profile Found!")
              : t('instagramShowcase.claim.readyToGo', "You're All Set!")}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('instagramShowcase.claim.instagramConnected', 'Instagram connected')} (@{flowData.instagramUsername})
          </Typography>
          <Typography variant="body2" paragraph>
            {t('instagramShowcase.claim.showcasesCreated', '{{count}} showcase(s) created', { count: flowData.showcaseIds?.length || 0 })}
          </Typography>
          <Typography variant="body2">
            {t('instagramShowcase.claim.businessCreated', 'Business profile initialized')}
          </Typography>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit(handleClaimAccount)}>
        {/* Email Field */}
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('instagramShowcase.claim.emailRequired', 'Email is required'),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('instagramShowcase.claim.emailInvalid', 'Please enter a valid email address')
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              margin="normal"
              label={t('instagramShowcase.claim.email', 'Email Address')}
              type="email"
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
                endAdornment: watchedEmail && (
                  <InputAdornment position="end">
                    {errors.email ? (
                      <Cancel color="error" />
                    ) : (
                      <CheckCircle color="success" />
                    )}
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Password Field */}
        <Controller
          name="password"
          control={control}
          rules={{
            required: t('instagramShowcase.claim.passwordRequired', 'Password is required'),
            minLength: {
              value: 8,
              message: t('instagramShowcase.claim.passwordMinLength', 'Password must be at least 8 characters')
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              margin="normal"
              label={t('instagramShowcase.claim.password', 'Password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Password Strength Indicator */}
        {watchedPassword && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('instagramShowcase.claim.passwordStrength', 'Password strength:')} {strengthLabels[passwordStrength.strength]}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(passwordStrength.strength / 5) * 100}
              color={strengthColors[passwordStrength.strength]}
              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        {/* Confirm Password Field */}
        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: t('instagramShowcase.claim.confirmPasswordRequired', 'Please confirm your password'),
            validate: (value: string) =>
              value === watchedPassword || t('instagramShowcase.claim.passwordsMismatch', 'Passwords do not match')
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              margin="normal"
              label={t('instagramShowcase.claim.confirmPassword', 'Confirm Password')}
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Terms and Conditions */}
        <Box sx={{ my: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isTncAccepted}
                onChange={(e) => setIsTncAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('instagramShowcase.claim.iAccept', 'I accept the')}{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setIsTncDialogOpen(true)}
                  sx={{ textTransform: 'none', p: 0, verticalAlign: 'baseline' }}
                >
                  {t('instagramShowcase.claim.termsAndConditions', 'Terms and Conditions')}
                </Button>
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isPrivacyPolicyAccepted}
                onChange={(e) => setIsPrivacyPolicyAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('instagramShowcase.claim.iAccept', 'I accept the')}{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setIsPrivacyPolicyDialogOpen(true)}
                  sx={{ textTransform: 'none', p: 0, verticalAlign: 'baseline' }}
                >
                  {t('instagramShowcase.claim.privacyPolicy', 'Privacy Policy')}
                </Button>
              </Typography>
            }
          />
        </Box>

        {/* Email Verification Notice */}
        <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            {t('instagramShowcase.claim.emailVerification', 'A verification email will be sent to your email address. Please check your inbox after account creation.')}
          </Typography>
        </Alert>

        {/* Navigation buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isLoading}
          >
            {t('instagramShowcase.claim.back', 'Back')}
          </Button>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading || !isTncAccepted || !isPrivacyPolicyAccepted}
            sx={{ px: 4 }}
          >
            {isLoading
              ? t('instagramShowcase.claim.claiming', 'Creating Account...')
              : t('instagramShowcase.claim.claimAccount', 'Create My Account')
            }
          </Button>
        </Box>
      </form>

      {/* Terms and Privacy Dialogs */}
      <TermsDialog
        isOpen={isTncDialogOpen}
        onClose={() => setIsTncDialogOpen(false)}
        currentLanguage={i18n.language}
        timestamp={Date.now()}
        t={{
          close: t('instagramShowcase.claim.close', 'Close'),
          termsAndConditions: t('instagramShowcase.claim.termsAndConditions', 'Terms and Conditions')
        }}
      />

      <PrivacyPolicyDialog
        isOpen={isPrivacyPolicyDialogOpen}
        onClose={() => setIsPrivacyPolicyDialogOpen(false)}
        currentLanguage={i18n.language}
        timestamp={Date.now()}
        t={{
          close: t('instagramShowcase.claim.close', 'Close'),
          privacyPolicy: t('instagramShowcase.claim.privacyPolicy', 'Privacy Policy')
        }}
      />
    </Box>
  );
};

export default AccountClaimStep;
